import * as THREE from 'three';

const cabinetLikeFrame = new THREE.Object3D();
const cabinetLikeWorldToLocal = new THREE.Matrix4();
const cabinetLikeCorner = new THREE.Vector3();
const cabinetLikeLocal = new THREE.Vector3();

/**
 * World-space origin matching a floor cabinet's `position` (near-min corner
 * in the laid-flat local frame). Placeholders stand upright and yaw on Y
 * with a GLB pivot — use each mesh's local AABB corners, not the world AABB
 * (world min.x / max.z jump to a different physical corner after rotation).
 * Height arrows key off this the same way cabinets key off `position`.
 *
 * Lives under helpers/placeholder so layout can import it without the
 * cabinetPosition ↔ layout circular `any`.
 * @param {THREE.Group} cabinetGroup - Cabinet or placeholder
 * @return {THREE.Vector3} World origin (clone)
 */
export const getCabinetLikeWorldOrigin = (
    cabinetGroup: THREE.Group
): THREE.Vector3 => {
    if (!cabinetGroup.userData?.isPlaceholder) {
        return cabinetGroup.position.clone();
    }

    cabinetGroup.updateMatrixWorld(true);
    cabinetLikeFrame.position.copy(cabinetGroup.position);
    cabinetLikeFrame.rotation.set(-Math.PI / 2, 0, cabinetGroup.rotation.y);
    cabinetLikeFrame.updateMatrixWorld(true);
    cabinetLikeWorldToLocal.copy(cabinetLikeFrame.matrixWorld).invert();

    let minLocalX = Infinity;
    let minLocalY = Infinity;
    let minLocalZ = Infinity;

    cabinetGroup.traverse((child) => {
        if (!child.visible || child === cabinetGroup) {
            return;
        }
        const mesh = child as THREE.Mesh;
        if (!mesh.isMesh || !mesh.geometry) {
            return;
        }
        if (!mesh.geometry.boundingBox) {
            mesh.geometry.computeBoundingBox();
        }
        const geoBox = mesh.geometry.boundingBox;
        if (!geoBox || geoBox.isEmpty()) {
            return;
        }

        for (let ix = 0; ix <= 1; ix++) {
            for (let iy = 0; iy <= 1; iy++) {
                for (let iz = 0; iz <= 1; iz++) {
                    cabinetLikeCorner.set(
                        ix ? geoBox.max.x : geoBox.min.x,
                        iy ? geoBox.max.y : geoBox.min.y,
                        iz ? geoBox.max.z : geoBox.min.z
                    );
                    cabinetLikeCorner.applyMatrix4(mesh.matrixWorld);
                    cabinetLikeLocal
                        .copy(cabinetLikeCorner)
                        .applyMatrix4(cabinetLikeWorldToLocal);
                    minLocalX = Math.min(minLocalX, cabinetLikeLocal.x);
                    minLocalY = Math.min(minLocalY, cabinetLikeLocal.y);
                    minLocalZ = Math.min(minLocalZ, cabinetLikeLocal.z);
                }
            }
        }
    });

    if (!isFinite(minLocalX) || !isFinite(minLocalY) || !isFinite(minLocalZ)) {
        return cabinetGroup.position.clone();
    }

    return cabinetLikeFrame.localToWorld(
        new THREE.Vector3(minLocalX, minLocalY, minLocalZ)
    );
};
